/**
 * Connect Page Component with Search Functionality
 * Allows users to discover and connect with other students
 * UPDATED: Added real search functionality with debouncing
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, 
  Filter, 
  UserPlus, 
  MessageSquare, 
  X, 
  ChevronRight, 
  Map, 
  Clock,
  BookOpen,
  Coffee,
  GraduationCap,
  Heart,
  Loader,
  Check,
  AlertCircle,
  UserX,
  Users,
  Book,
  Camera,
  Music,
  Globe,
  Wine,
  Utensils,
  Dumbbell,
  Home,
  Eye,
  Send
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import useProfiles from '../hooks/useProfiles';
import useConnections from '../hooks/useConnections';
import { Link, useNavigate } from 'react-router-dom';
import connectionService from '../services/connectionService';
import { toast } from 'react-hot-toast';

// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Filter options
const filterOptions = {
  universities: [
    'All Universities',
    'University of Cape Town',
    'University of the Western Cape',
    'Stellenbosch University',
    'University of Johannesburg',
    'University of the Witwatersrand'
  ],
  courses: [
    'All Courses',
    'Computer Science',
    'Engineering',
    'Law',
    'Medicine',
    'Business',
    'Arts',
    'Biology'
  ],
  interests: [
    'Coding',
    'Sports',
    'Music',
    'Reading',
    'Art',
    'Gaming',
    'Hiking',
    'Travel',
    'Cooking',
    'Photography'
  ],
  connectionType: [
    'All Types',
    'Study Partners',
    'Social Connections',
    'Tutoring',
    'Mentorship'
  ],
  personalities: [
    'Outgoing',
    'Introvert',
    'Extrovert',
    'Adventurous',
    'Quiet',
    'Analytical',
    'Creative',
    'Organized',
    'Spontaneous',
    'Logical'
  ]
};

// Connection reasons with icons
const connectionReasons = [
  { id: 'study', label: 'Study Together', icon: Book, color: 'bg-blue-100 text-blue-600' },
  { id: 'gym', label: 'Gym Partners', icon: Dumbbell, color: 'bg-red-100 text-red-600' },
  { id: 'coffee', label: 'Coffee Chat', icon: Coffee, color: 'bg-amber-100 text-amber-600' },
  { id: 'campus', label: 'Campus & Chill', icon: Home, color: 'bg-green-100 text-green-600' },
  { id: 'icecream', label: 'Ice Cream Walk', icon: Utensils, color: 'bg-pink-100 text-pink-600' },
  { id: 'lunch', label: 'Lunch Buddies', icon: Utensils, color: 'bg-orange-100 text-orange-600' },
  { id: 'project', label: 'Project Collaboration', icon: Users, color: 'bg-violet-100 text-violet-600' },
  { id: 'photography', label: 'Photography Session', icon: Camera, color: 'bg-indigo-100 text-indigo-600' },
  { id: 'music', label: 'Music Jam Session', icon: Music, color: 'bg-purple-100 text-purple-600' },
  { id: 'travel', label: 'Weekend Adventure', icon: Globe, color: 'bg-cyan-100 text-cyan-600' },
  { id: 'dinner', label: 'Dinner Plans', icon: Utensils, color: 'bg-rose-100 text-rose-600' },
  { id: 'mentorship', label: 'Academic Mentorship', icon: GraduationCap, color: 'bg-emerald-100 text-emerald-600' },
  { id: 'clubnight', label: 'Club Night Out', icon: Music, color: 'bg-fuchsia-100 text-fuchsia-600' },
  { id: 'movie', label: 'Movie Night', icon: Camera, color: 'bg-slate-100 text-slate-600' },
  { id: 'gaming', label: 'Gaming Session', icon: Globe, color: 'bg-lime-100 text-lime-600' },
  { id: 'networking', label: 'Career Networking', icon: Users, color: 'bg-teal-100 text-teal-600' }
];

/**
 * Connect Page Component
 * 
 * @returns {JSX.Element} Connect page component
 */
const ConnectPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    university: 'All Universities',
    course: 'All Courses',
    connectionType: 'All Types',
    interests: [],
    personalities: [],
    onlyOnline: false
  });

  // Connection request modal state
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Profile viewing modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  
  // Connection state management
  const [requestStates, setRequestStates] = useState({});
  const [statesLoading, setStatesLoading] = useState(false);

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300); // 300ms debounce

  // Ref for maintaining scroll position
  const profilesContainerRef = useRef(null);
  const lastLoadPositionRef = useRef(0);
  const searchInputRef = useRef(null);

  // Use our custom hooks to get profiles and connections
  const { 
    profiles, 
    loading: profilesLoading, 
    error: profilesError, 
    loadMore,
    hasMore,
    refreshProfiles,
    clearSearch,
    searchStats
  } = useProfiles({
    ...filters,
    searchQuery: debouncedSearchQuery
  });

  const {
    connections,
    pendingRequests,
    loading: connectionsLoading,
    error: connectionsError,
    checkConnectionStatus,
    sendConnectionRequest,
    acceptRequest,
    declineRequest,
    removeConnection,
    refreshConnections,
    refreshPendingRequests
  } = useConnections();
  
  // Check URL parameters for active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam && ['discover', 'connections', 'requests'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  // Update searching state when search query changes
  useEffect(() => {
    setIsSearching(searchQuery.trim().length > 0);
  }, [searchQuery]);

  // Connection status checking with proper state management
  const checkConnectionStatusesForProfiles = useCallback(async (profilesToCheck) => {
    if (!profilesToCheck || !currentUser || profilesToCheck.length === 0) return;
    
    setStatesLoading(true);
    const states = {};
    
    try {
      const statusChecks = profilesToCheck.map(async (profile) => {
        if (profile.id !== currentUser.uid) {
          const status = await checkConnectionStatus(profile.id);
          return { profileId: profile.id, status };
        }
        return null;
      });
      
      const results = await Promise.all(statusChecks);
      
      results.forEach(result => {
        if (result) {
          states[result.profileId] = result.status;
        }
      });
      
      setRequestStates(prevStates => ({
        ...prevStates,
        ...states
      }));
      
    } catch (error) {
      console.error('Error checking connection statuses:', error);
    } finally {
      setStatesLoading(false);
    }
  }, [currentUser, checkConnectionStatus]);

  // Check connection statuses when profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0) {
      checkConnectionStatusesForProfiles(profiles);
    }
  }, [profiles, checkConnectionStatusesForProfiles]);

  // Update connection states when connections or pending requests change
  useEffect(() => {
    if (connections && connections.length > 0) {
      const connectedStates = {};
      connections.forEach(conn => {
        connectedStates[conn.userId] = { status: 'connected' };
      });
      
      setRequestStates(prevStates => ({
        ...prevStates,
        ...connectedStates
      }));
    }
  }, [connections]);

  useEffect(() => {
    if (pendingRequests && pendingRequests.length > 0) {
      const pendingStates = {};
      pendingRequests.forEach(req => {
        pendingStates[req.senderId] = { status: 'pending', direction: 'received' };
      });
      
      setRequestStates(prevStates => ({
        ...prevStates,
        ...pendingStates
      }));
    }
  }, [pendingRequests]);
  
  // Get connected user IDs for filtering
  const getConnectedUserIds = () => {
    return connections.map(conn => conn.userId);
  };

  // Get pending request sender IDs (people who sent us requests)
  const getPendingRequestSenderIds = () => {
    return pendingRequests.map(req => req.senderId);
  };

  // Filter profiles to exclude connected users from discover tab
  const getFilteredProfiles = () => {
    if (!profiles) return [];
    
    const connectedUserIds = getConnectedUserIds();
    
    return profiles.filter(profile => {
      // Exclude current user
      if (profile.id === currentUser?.uid) return false;
      
      // For discover tab, exclude already connected users
      if (activeTab === 'discover' && connectedUserIds.includes(profile.id)) {
        return false;
      }
      
      return true;
    });
  };
  
  // View a profile in detail
  const openProfileModal = (profile) => {
    setViewingProfile(profile);
    setShowProfileModal(true);
  };
  
  // Close profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setViewingProfile(null);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled automatically via debounced query
    if (searchInputRef.current) {
      searchInputRef.current.blur(); // Remove focus from input
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Apply filters
  const applyFilters = () => {
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      university: 'All Universities',
      course: 'All Courses',
      connectionType: 'All Types',
      interests: [],
      personalities: [],
      onlyOnline: false
    });
  };

  // Toggle interest selection
  const toggleInterest = (interest) => {
    if (filters.interests.includes(interest)) {
      setFilters({
        ...filters,
        interests: filters.interests.filter(i => i !== interest)
      });
    } else {
      setFilters({
        ...filters,
        interests: [...filters.interests, interest]
      });
    }
  };
  
  // Toggle personality selection
  const togglePersonality = (personality) => {
    if (filters.personalities.includes(personality)) {
      setFilters({
        ...filters,
        personalities: filters.personalities.filter(p => p !== personality)
      });
    } else {
      setFilters({
        ...filters,
        personalities: [...filters.personalities, personality]
      });
    }
  };

  // Open connection request modal
  const openConnectionModal = (profile) => {
    setSelectedProfile(profile);
    setSelectedReason(null);
    setCustomMessage('');
    setShowConnectionModal(true);
  };

  // Close connection request modal
  const closeConnectionModal = () => {
    setShowConnectionModal(false);
    setSelectedProfile(null);
    setSelectedReason(null);
    setCustomMessage('');
  };

  // Toggle connection reason selection
  const toggleReason = (reasonId) => {
    if (selectedReason === reasonId) {
      setSelectedReason(null);
    } else {
      setSelectedReason(reasonId);
      
      const reason = connectionReasons.find(r => r.id === reasonId);
      if (reason) {
        setCustomMessage(`Hey! I'd like to connect as ${reason.label.toLowerCase()} partners.`);
      }
    }
  };

  // Handle starting a message with a user
  const handleStartMessageWithUser = async (userId) => {
    try {
      await connectionService.startConversationWithUser(userId, navigate);
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error(error.message || 'Failed to start conversation');
    }
  };

  // Handle sending a connection request with immediate state update
  const handleSendRequest = async () => {
    if (!selectedProfile) return;
    
    try {
      setSendingRequest(true);
      
      // IMMEDIATELY update the UI state
      setRequestStates(prev => ({
        ...prev,
        [selectedProfile.id]: { status: 'pending', direction: 'sent' }
      }));
      
      const message = customMessage.trim();
      await sendConnectionRequest(selectedProfile.id, message);
      
      toast.success('Connection request sent!');
      closeConnectionModal();
      refreshProfiles();
      
    } catch (error) {
      console.error('Error sending connection request:', error);
      
      // REVERT the state change if request failed
      setRequestStates(prev => ({
        ...prev,
        [selectedProfile.id]: { status: 'none' }
      }));
      
      toast.error(error.message || 'Failed to send request');
    } finally {
      setSendingRequest(false);
    }
  };

  // Handle accepting a connection request with immediate state update
  const handleAcceptRequest = async (requestId, profileId) => {
    try {
      // IMMEDIATELY update the UI state
      setRequestStates(prev => ({
        ...prev,
        [profileId]: { status: 'connected' }
      }));
      
      await acceptRequest(requestId);
      refreshPendingRequests();
      refreshConnections();
      toast.success('Connection request accepted!');
      
    } catch (error) {
      console.error('Error accepting request:', error);
      
      // REVERT the state change if request failed
      setRequestStates(prev => ({
        ...prev,
        [profileId]: { status: 'pending', direction: 'received' }
      }));
      
      toast.error('Failed to accept request');
    }
  };

  // Handle declining a connection request with immediate state update
  const handleDeclineRequest = async (requestId, profileId) => {
    try {
      // IMMEDIATELY update the UI state
      setRequestStates(prev => ({
        ...prev,
        [profileId]: { status: 'none' }
      }));
      
      await declineRequest(requestId);
      refreshPendingRequests();
      toast.success('Connection request declined');
      
    } catch (error) {
      console.error('Error declining request:', error);
      
      // REVERT the state change if request failed
      setRequestStates(prev => ({
        ...prev,
        [profileId]: { status: 'pending', direction: 'received' }
      }));
      
      toast.error('Failed to decline request');
    }
  };

  // Handle removing a connection with immediate state update
  const handleRemoveConnection = async (userId) => {
    if (window.confirm('Are you sure you want to remove this connection?')) {
      try {
        // IMMEDIATELY update the UI state
        setRequestStates(prev => ({
          ...prev,
          [userId]: { status: 'none' }
        }));
        
        await removeConnection(userId);
        refreshConnections();
        toast.success('Connection removed');
        
      } catch (error) {
        console.error('Error removing connection:', error);
        
        // REVERT the state change if request failed
        setRequestStates(prev => ({
          ...prev,
          [userId]: { status: 'connected' }
        }));
        
        toast.error('Failed to remove connection');
      }
    }
  };

  // Handle accepting request from discover tab
  const handleAcceptFromDiscover = async (profileId) => {
    const pendingRequest = pendingRequests.find(req => req.senderId === profileId);
    
    if (pendingRequest) {
      await handleAcceptRequest(pendingRequest.id, profileId);
    }
  };

  // Handle load more profiles
  const handleLoadMore = () => {
    if (profilesContainerRef.current) {
      lastLoadPositionRef.current = profilesContainerRef.current.scrollHeight;
    }
    loadMore();
  };

  // Restore scroll position after loading more profiles
  useEffect(() => {
    if (!profilesLoading && lastLoadPositionRef.current > 0 && profilesContainerRef.current) {
      const scrollTo = lastLoadPositionRef.current;
      
      setTimeout(() => {
        window.scrollTo(0, scrollTo);
        lastLoadPositionRef.current = 0;
      }, 100);
    }
  }, [profiles, profilesLoading]);

  // Render connect button with proper state checking
  const renderConnectButton = (profileId) => {
    // Check if this person sent us a request first (highest priority)
    const pendingRequestSenderIds = getPendingRequestSenderIds();
    if (pendingRequestSenderIds.includes(profileId)) {
      return (
        <button 
          onClick={() => handleAcceptFromDiscover(profileId)}
          className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center"
        >
          <Check size={12} className="mr-1" /> Accept Request
        </button>
      );
    }
    
    // Check connection status from our state
    const requestStatus = requestStates[profileId];
    
    // Show loading state while checking status
    if (statesLoading && !requestStatus) {
      return (
        <button 
          disabled
          className="bg-gray-200 text-gray-500 text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center cursor-not-allowed"
        >
          <Loader size={12} className="animate-spin mr-1" /> Loading...
        </button>
      );
    }
    
    // Handle different connection statuses
    switch (requestStatus?.status) {
      case 'connected':
        return (
          <button 
            onClick={() => handleRemoveConnection(profileId)}
            className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center"
          >
            <UserX size={12} className="mr-1" /> Connected
          </button>
        );
      
      case 'pending':
        if (requestStatus.direction === 'sent') {
          return (
            <button 
              disabled
              className="bg-gray-300 text-gray-600 text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center cursor-not-allowed"
            >
              <Clock size={12} className="mr-1" /> Request Sent
            </button>
          );
        } else {
          return (
            <button 
              onClick={() => handleAcceptFromDiscover(profileId)}
              className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center"
            >
              <Check size={12} className="mr-1" /> Accept Request
            </button>
          );
        }
        default:
        // No connection status or status is 'none'
        return (
          <button 
            onClick={() => openConnectionModal({ id: profileId, ...profiles.find(p => p.id === profileId) })}
            className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded-md flex items-center flex-1 sm:flex-initial justify-center"
          >
            <UserPlus size={12} className="mr-1" /> Connect
          </button>
        );
    }
  };

  // Get filtered profiles for display
  const filteredProfiles = getFilteredProfiles();

  return (
    <div className="pb-20" ref={profilesContainerRef}>
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg mb-4 p-4">
        <h1 className="text-xl font-bold text-gray-800 mb-4">Connect with Students</h1>
        
        {/* Enhanced Search Bar */}
        <form onSubmit={handleSearch} className="relative mb-4">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name, username, university, or interests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-20 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          
          {/* Search Actions */}
          <div className="absolute inset-y-0 right-0 pr-2 flex items-center space-x-1">
            {/* Clear Search Button */}
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
            
            {/* Search Submit Button */}
            <button 
              type="submit" 
              className="bg-green-500 text-white p-2 rounded-md hover:bg-green-600 transition-colors"
              title="Search"
            >
              <Search size={16} />
            </button>
          </div>
        </form>

        {/* Search Results Info */}
        {isSearching && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Search size={16} className="text-blue-600" />
                <span className="text-sm text-blue-800 font-medium">
                  {profilesLoading ? (
                    'Searching...'
                  ) : (
                    `Found ${searchStats.searchResults} result${searchStats.searchResults !== 1 ? 's' : ''} for "${debouncedSearchQuery}"`
                  )}
                </span>
              </div>
              
              {!profilesLoading && (
                <button
                  onClick={handleClearSearch}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Clear search
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Filter Button */}
        <button
          onClick={() => setShowFilters(true)}
          className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
        >
          <Filter size={18} />
          <span>Filter Students</span>
          {/* Filter badges */}
          {(filters.university !== 'All Universities' || 
            filters.course !== 'All Courses' || 
            filters.interests.length > 0 || 
            filters.personalities.length > 0 || 
            filters.onlyOnline) && (
            <span className="bg-green-500 text-white text-xs rounded-full px-2 py-0.5 ml-2">
              Active
            </span>
          )}
        </button>
      </div>
      
      {/* Tabs */}
      <div className="bg-white shadow-sm rounded-lg mb-4 overflow-hidden">
        <div className="flex border-b">
          <button 
            className={`flex-1 py-3 text-center font-medium text-sm ${
              activeTab === 'discover' 
                ? 'text-green-500 border-b-2 border-green-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('discover')}
          >
            Discover
          </button>
          <button 
            className={`flex-1 py-3 text-center font-medium text-sm ${
              activeTab === 'connections' 
                ? 'text-green-500 border-b-2 border-green-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('connections')}
          >
            Connections {connections.length > 0 && <span className="bg-gray-200 text-gray-700 text-xs rounded-full px-1.5 ml-1">{connections.length}</span>}
          </button>
          <button 
            className={`flex-1 py-3 text-center font-medium text-sm ${
              activeTab === 'requests' 
                ? 'text-green-500 border-b-2 border-green-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('requests')}
          >
            Requests {pendingRequests.length > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5 ml-1">{pendingRequests.length}</span>}
          </button>
        </div>

        {/* Content based on active tab */}
        <div className="p-4">
          {/* Discover Tab */}
          {activeTab === 'discover' && (
            <div>
              {profilesLoading && filteredProfiles.length === 0 ? (
                <div className="text-center py-12">
                  <Loader className="h-8 w-8 text-green-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">
                    {isSearching ? 'Searching profiles...' : 'Loading profiles...'}
                  </p>
                </div>
              ) : profilesError ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500 font-medium mb-2">Error loading profiles</p>
                  <p className="text-gray-500">{profilesError}</p>
                  <button 
                    onClick={refreshProfiles}
                    className="mt-4 bg-green-500 text-white px-4 py-2 rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              ) : filteredProfiles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredProfiles.map(profile => (
                    <div 
                      key={profile.id} 
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow p-3"
                    >
                      <div className="flex items-center space-x-3">
                        {/* Profile Image */}
                        <div className="relative flex-shrink-0">
                          <img 
                            src={profile.photos && profile.photos.length > 0 
                              ? profile.photos[0]
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                            alt={profile.basicInfo?.name || 'User'} 
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          {profile.preferences?.activityStatus === "Available" && (
                            <div className="absolute bottom-0 right-0 bg-green-500 border-2 border-white w-3 h-3 rounded-full"></div>
                          )}
                        </div>
                        
                        {/* Profile Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {profile.basicInfo?.name || 'User'}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            @{profile.basicInfo?.username || 'username'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-2">
                        <div className="text-xs text-gray-600 flex items-center mb-1 truncate">
                          <GraduationCap size={12} className="mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {profile.academic?.university || 'University'}
                          </span>
                        </div>
                        
                        <p className="text-xs text-gray-600 line-clamp-1 mb-2">
                          {profile.bio?.shortBio || 'No bio available'}
                        </p>
                      </div>
                      
                      {/* Personalities - condensed */}
                      {profile.bio?.personalities && profile.bio.personalities.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {profile.bio.personalities.slice(0, 2).map((personality, i) => (
                            <span 
                              key={i} 
                              className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full text-[10px]"
                            >
                              {personality}
                            </span>
                          ))}
                          {profile.bio.personalities.length > 2 && (
                            <span className="text-xs text-gray-500 text-[10px]">
                              +{profile.bio.personalities.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-2 space-x-1">
                        {renderConnectButton(profile.id)}
                        
                        <button 
                          onClick={() => openProfileModal(profile)}
                          className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <Eye size={12} className="mr-1" /> View
                        </button>
                        
                        {/* Only show message button if connected */}
                        {requestStates[profile.id]?.status === 'connected' && (
                          <button
                            onClick={() => handleStartMessageWithUser(profile.id)}
                            className="border border-gray-300 hover:bg-gray-50 text-blue-600 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                          >
                            <MessageSquare size={12} className="mr-1" /> Message
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-gray-800 font-medium mb-1">
                    {isSearching ? 'No search results found' : 'No results found'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {isSearching 
                      ? `No students match your search for "${debouncedSearchQuery}"`
                      : 'Try adjusting your search or filters to find more students'
                    }
                  </p>
                  
                  <div className="mt-4 space-x-2">
                    {isSearching && (
                      <button 
                        onClick={handleClearSearch}
                        className="text-green-500 font-medium text-sm hover:text-green-600"
                      >
                        Clear Search
                      </button>
                    )}
                    <button 
                      onClick={resetFilters}
                      className="text-green-500 font-medium text-sm hover:text-green-600"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              )}
              
              {/* Load More Button - Only show when not searching */}
              {hasMore && !isSearching && (
                <div className="text-center pt-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={profilesLoading}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium"
                  >
                    {profilesLoading ? (
                      <span className="flex items-center justify-center">
                        <Loader size={14} className="animate-spin mr-2" /> Loading...
                      </span>
                    ) : (
                      'Load More Profiles'
                    )}
                  </button>
                </div>
              )}

              {/* Search hint when searching */}
              {isSearching && filteredProfiles.length > 0 && (
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-500">
                    Showing search results for "{debouncedSearchQuery}"
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div>
              {connectionsLoading ? (
                <div className="text-center py-12">
                  <Loader className="h-8 w-8 text-green-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Loading connections...</p>
                </div>
              ) : connectionsError ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500 font-medium mb-2">Error loading connections</p>
                  <p className="text-gray-500">{connectionsError}</p>
                  <button 
                    onClick={refreshConnections}
                    className="mt-4 bg-green-500 text-white px-4 py-2 rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              ) : connections.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {connections.map(connection => (
                    <div 
                      key={connection.id} 
                      className="bg-white border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-center space-x-3">
                        <img 
                          src={connection.profile.photos && connection.profile.photos.length > 0 
                            ? connection.profile.photos[0]
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(connection.profile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                          alt={connection.profile.basicInfo?.name} 
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {connection.profile.basicInfo?.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            @{connection.profile.basicInfo?.username}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {connection.profile.academic?.university}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center mt-3 space-x-1">
                        <button 
                          onClick={() => openProfileModal(connection.profile)}
                          className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <Eye size={12} className="mr-1" /> View
                        </button>
                        <button 
                          onClick={() => handleStartMessageWithUser(connection.userId)}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <MessageSquare size={12} className="mr-1" /> Message
                        </button>
                        <button 
                          onClick={() => handleRemoveConnection(connection.userId)}
                          className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <UserX size={12} className="mr-1" /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <UserPlus className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-gray-800 font-medium mb-1">No connections yet</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Start connecting with students to build your network
                  </p>
                  <button 
                    onClick={() => setActiveTab('discover')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium"
                  >
                    Discover Students
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div>
              {connectionsLoading ? (
                <div className="text-center py-12">
                  <Loader className="h-8 w-8 text-green-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Loading requests...</p>
                </div>
              ) : connectionsError ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500 font-medium mb-2">Error loading requests</p>
                  <p className="text-gray-500">{connectionsError}</p>
                  <button 
                    onClick={refreshPendingRequests}
                    className="mt-4 bg-green-500 text-white px-4 py-2 rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              ) : pendingRequests.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingRequests.map(request => (
                    <div 
                      key={request.id} 
                      className="bg-white border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-center space-x-3">
                        <img 
                          src={request.profile.photos && request.profile.photos.length > 0 
                            ? request.profile.photos[0]
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(request.profile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                          alt={request.profile.basicInfo?.name} 
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {request.profile.basicInfo?.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            @{request.profile.basicInfo?.username}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {request.profile.academic?.university}
                          </p>
                        </div>
                      </div>
                      
                      {request.message && (
                        <div className="mt-2 bg-gray-50 p-2 rounded-md">
                          <p className="text-xs text-gray-600 italic">
                            "{request.message}"
                          </p>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-3 space-x-1">
                        <button 
                          onClick={() => openProfileModal(request.profile)}
                          className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <Eye size={12} className="mr-1" /> View
                        </button>
                        <button 
                          onClick={() => handleAcceptRequest(request.id, request.senderId)}
                          className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <Check size={12} className="mr-1" /> Accept
                        </button>
                        <button 
                          onClick={() => handleDeclineRequest(request.id, request.senderId)}
                          className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-md flex items-center flex-1 justify-center"
                        >
                          <X size={12} className="mr-1" /> Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <UserPlus className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-gray-800 font-medium mb-1">No pending requests</h3>
                  <p className="text-gray-500 text-sm">
                    When someone wants to connect with you, you'll see their request here
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

{/* Filter Modal - Enhanced with active filter indicators */}
      {showFilters && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowFilters(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:rounded-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Filter Students</h3>
                  <button 
                    onClick={() => setShowFilters(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                <div className="space-y-4 max-h-[calc(80vh-150px)] overflow-y-auto pr-2">
                  {/* University Filter */}
                  <div>
                    <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-1">
                      University
                      {filters.university !== 'All Universities' && (
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </label>
                    <select
                      id="university"
                      value={filters.university}
                      onChange={(e) => setFilters({...filters, university: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    >
                      {filterOptions.universities.map(university => (
                        <option key={university} value={university}>
                          {university}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Course Filter */}
                  <div>
                    <label htmlFor="course" className="block text-sm font-medium text-gray-700 mb-1">
                      Course
                      {filters.course !== 'All Courses' && (
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </label>
                    <select
                      id="course"
                      value={filters.course}
                      onChange={(e) => setFilters({...filters, course: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    >
                      {filterOptions.courses.map(course => (
                        <option key={course} value={course}>
                          {course}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Connection Type Filter */}
                  <div>
                    <label htmlFor="connectionType" className="block text-sm font-medium text-gray-700 mb-1">
                      Connection Type
                      {filters.connectionType !== 'All Types' && (
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </label>
                    <select
                      id="connectionType"
                      value={filters.connectionType}
                      onChange={(e) => setFilters({...filters, connectionType: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    >
                      {filterOptions.connectionType.map(type => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Personalities Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personalities
                      {filters.personalities.length > 0 && (
                        <span className="ml-2 bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full">
                          {filters.personalities.length} selected
                        </span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.personalities.map(personality => (
                        <button
                          key={personality}
                          type="button"
                          onClick={() => togglePersonality(personality)}
                          className={`text-xs px-3 py-1.5 rounded-full ${
                            filters.personalities.includes(personality)
                              ? 'bg-purple-100 text-purple-800 border border-purple-300'
                              : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {personality}
                        </button>
                      ))}
                      </div>
                  </div>
                  
                  {/* Interests Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interests
                      {filters.interests.length > 0 && (
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          {filters.interests.length} selected
                        </span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.interests.map(interest => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleInterest(interest)}
                          className={`text-xs px-3 py-1.5 rounded-full ${
                            filters.interests.includes(interest)
                              ? 'bg-green-100 text-green-800 border border-green-300'
                              : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Online Status Filter */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="onlineOnly"
                      checked={filters.onlineOnly}
                      onChange={(e) => setFilters({...filters, onlineOnly: e.target.checked})}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="onlineOnly" className="ml-2 block text-sm text-gray-700">
                      Show only online students
                      {filters.onlyOnline && (
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-500 text-base font-medium text-white hover:bg-green-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connection Request Modal */}
      {showConnectionModal && selectedProfile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={closeConnectionModal}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:rounded-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Connect with {selectedProfile.basicInfo?.name}</h3>
                  <button 
                    onClick={closeConnectionModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
                  <img 
                    src={selectedProfile.photos && selectedProfile.photos.length > 0 
                      ? selectedProfile.photos[0]
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                    alt={selectedProfile.basicInfo?.name} 
                    className="w-14 h-14 rounded-full object-cover mr-3"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedProfile.basicInfo?.name}</h4>
                    <p className="text-xs text-gray-500 mb-1">
                      {selectedProfile.academic?.university} • {selectedProfile.academic?.degree}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Why do you want to connect?</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                    {connectionReasons.map(reason => (
                      <button
                        key={reason.id}
                        type="button"
                        onClick={() => toggleReason(reason.id)}
                        className={`text-xs p-2 rounded-lg flex items-center border ${
                          selectedReason === reason.id
                            ? `${reason.color} border-opacity-50`
                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <reason.icon size={14} className="mr-1.5 flex-shrink-0" />
                        <span className="truncate">{reason.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 mb-1">
                    Add a personal message (optional)
                  </label>
                  <textarea
                    id="customMessage"
                    rows={3}
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="Hey there! I'd love to connect because..."
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeConnectionModal}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendRequest}
                    disabled={sendingRequest}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-500 text-sm font-medium text-white hover:bg-green-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingRequest ? (
                      <span className="flex items-center">
                        <Loader size={14} className="animate-spin mr-2" /> Sending...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Send size={14} className="mr-2" /> Send Request
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal - UPDATED TO HIDE EMAIL */}
      {showProfileModal && viewingProfile && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={closeProfileModal}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-t-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:rounded-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Profile Details</h3>
                  <button 
                    onClick={closeProfileModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  {/* Profile Header */}
                  <div className="flex items-center">
                    <img 
                      src={viewingProfile.photos && viewingProfile.photos.length > 0 
                        ? viewingProfile.photos[0]
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(viewingProfile.basicInfo?.name || 'User')}&background=4CAF50&color=fff`} 
                      alt={viewingProfile.basicInfo?.name} 
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {viewingProfile.basicInfo?.name || 'User'}
                      </h2>
                      <p className="text-sm text-gray-500">
                        @{viewingProfile.basicInfo?.username || 'username'}
                      </p>
                    </div>
                  </div>

                  {/* Basic Info - EMAIL REMOVED FOR PRIVACY */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-md font-semibold text-gray-700 mb-2">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <div className="text-gray-500">Gender</div>
                      <div className="text-gray-700">{viewingProfile.basicInfo?.gender || 'N/A'}</div>
                      
                      {viewingProfile.basicInfo?.interestedIn && viewingProfile.basicInfo.interestedIn.length > 0 && (
                        <>
                          <div className="text-gray-500">Interested In</div>
                          <div className="text-gray-700">{viewingProfile.basicInfo.interestedIn.join(', ')}</div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-md font-semibold text-gray-700 mb-2">Academic Information</h3>
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <div className="text-gray-500">University</div>
                      <div className="text-gray-700">{viewingProfile.academic?.university || 'N/A'}</div>
                      
                      <div className="text-gray-500">Degree</div>
                      <div className="text-gray-700">{viewingProfile.academic?.degree || 'N/A'}</div>
                      
                      <div className="text-gray-500">Year of Study</div>
                      <div className="text-gray-700">{viewingProfile.academic?.yearOfStudy || 'N/A'}</div>
                      
                      <div className="text-gray-500">Residence</div>
                      <div className="text-gray-700">{viewingProfile.academic?.residence || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-md font-semibold text-gray-700 mb-2">About Me</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      {viewingProfile.bio?.shortBio || 'No short bio available'}
                    </p>
                    
                    {viewingProfile.bio?.longBio && (
                      <p className="text-sm text-gray-700 mb-3">
                        {viewingProfile.bio.longBio}
                      </p>
                    )}
                    
                    {/* Personalities */}
                    {viewingProfile.bio?.personalities && viewingProfile.bio.personalities.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-600 mb-1">Personality</h4>
                        <div className="flex flex-wrap gap-1">
                          {viewingProfile.bio.personalities.map((personality, idx) => (
                            <span 
                              key={idx}
                              className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full"
                            >
                              {personality}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Interests */}
                    {viewingProfile.bio?.interests && viewingProfile.bio.interests.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-1">Interests</h4>
                        <div className="flex flex-wrap gap-1">
                          {viewingProfile.bio.interests.map((interest, idx) => (
                            <span 
                              key={idx}
                              className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Languages */}
                  {viewingProfile.languages && viewingProfile.languages.length > 0 && (
                    <div className="border-t border-gray-100 pt-4">
                      <h3 className="text-md font-semibold text-gray-700 mb-2">Languages</h3>
                      <div className="flex flex-wrap gap-1">
                        {viewingProfile.languages.map((language, idx) => (
                          <span 
                            key={idx}
                            className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                          >
                            {language}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex space-x-2">
                  {/* Action buttons based on connection status */}
                  {requestStates[viewingProfile.id]?.status === 'none' && !getPendingRequestSenderIds().includes(viewingProfile.id) && (
                    <button
                      onClick={() => {
                        closeProfileModal();
                        openConnectionModal(viewingProfile);
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center"
                    >
                      <UserPlus size={16} className="mr-2" /> Connect
                    </button>
                  )}
                  
                  {/* Show accept button if they sent us a request */}
                  {getPendingRequestSenderIds().includes(viewingProfile.id) && (
                    <button
                      onClick={() => {
                        closeProfileModal();
                        handleAcceptFromDiscover(viewingProfile.id);
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center"
                    >
                      <Check size={16} className="mr-2" /> Accept Request
                    </button>
                  )}
                  
                  {requestStates[viewingProfile.id]?.status === 'pending' && (
                    <button
                      disabled
                      className="flex-1 bg-gray-300 text-gray-600 rounded-md py-2 text-sm font-medium flex items-center justify-center cursor-not-allowed"
                    >
                      <Clock size={16} className="mr-2" /> Request Sent
                    </button>
                  )}
                  
                  {requestStates[viewingProfile.id]?.status === 'connected' && (
                    <>
                      <button
                        onClick={() => {
                          closeProfileModal();
                          handleStartMessageWithUser(viewingProfile.id);
                        }}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md py-2 text-sm font-medium flex items-center justify-center"
                      >
                        <MessageSquare size={16} className="mr-2" /> Message
                      </button>
                      
                      <button
                        onClick={() => {
                          closeProfileModal();
                          handleRemoveConnection(viewingProfile.id);
                        }}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md py-2 text-sm font-medium flex items-center justify-center"
                      >
                        <UserX size={16} className="mr-2" /> Remove Connection
                      </button>
                    </>
                  )}
                  
                  {(!requestStates[viewingProfile.id] || requestStates[viewingProfile.id]?.status === 'none') && !getPendingRequestSenderIds().includes(viewingProfile.id) && (
                    <button
                      onClick={closeProfileModal}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md py-2 text-sm font-medium flex items-center justify-center"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectPage;